/* Flat inline-name bracelet illustration.
 * Preserves the curb chain, lobster clasp and extender.
 * Letter bounds target 52 SVG units, capped below 60 units, compared with
 * approximately 17.4 units for a link's outside height: about 3–3.5x.
 * These are illustrative proportions, not verified physical dimensions.
 */
(function () {
  'use strict';

  const NB = window.NB = window.NB || {};
  const NS = 'http://www.w3.org/2000/svg';
  const W = 1200, H = 300;
  const X0 = 40, X1 = 1160, Y0 = 120, SAG = 46;
  const halfSpan = (X1 - X0) / 2;
  const midpoint = (X0 + X1) / 2;
  const curveY = x =>
    Y0 + SAG * (1 - Math.pow((x - midpoint) / halfSpan, 2));
  const curveA = x =>
    Math.atan(-2 * SAG * (x - midpoint) / (halfSpan * halfSpan))
      * 180 / Math.PI;
  let serial = 0;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const loaded = new Set();
  const pending = new Map();
  const fontSpec = (font, size) =>
    `${font.ital ? 'italic ' : ''}${font.weight || 400} ${size}px "${font.family}"`;

  NB.fonts = {
    key(font) {
      return `${font.family}:${font.weight || 400}:${Boolean(font.ital)}`;
    },
    isLoaded(font) {
      return loaded.has(this.key(font));
    },
    ensure(font) {
      const key = this.key(font);
      if (loaded.has(key)) return Promise.resolve(true);
      if (pending.has(key)) return pending.get(key);
      const promise = (async () => {
        if (!document.fonts?.load) return false;
        try {
          const faces = await document.fonts.load(
            fontSpec(font, 40), font.sample || 'AaEmma'
          );
          const ok = faces.length > 0;
          if (ok) loaded.add(key);
          return ok;
        } catch {
          return false;
        }
      })();
      pending.set(key, promise);
      promise.then(ok => { if (!ok) pending.delete(key); });
      return promise;
    },
    metrics(text, font, size) {
      context.font = fontSpec(font, size);
      const measured = context.measureText(text || 'Name');
      const ascent = measured.actualBoundingBoxAscent;
      const descent = measured.actualBoundingBoxDescent;
      const height = ascent + descent;
      return {
        width: Math.max(
          measured.width,
          (measured.actualBoundingBoxLeft || 0)
            + (measured.actualBoundingBoxRight || 0)
        ),
        height: Number.isFinite(height) && height > 0 ? height : size,
        ascent: Number.isFinite(ascent) ? ascent : size * 0.8,
        descent: Number.isFinite(descent) ? descent : size * 0.2
      };
    },
    measure(text, font, size) {
      return this.metrics(text, font, size).width;
    },
    css(font) {
      return {
        fontFamily: `"${font.family}", ${font.cat === 'block' ? 'serif' : 'cursive'}`,
        fontWeight: font.weight || 400,
        fontStyle: font.ital ? 'italic' : 'normal'
      };
    }
  };

  function node(tag, attributes = {}, parent) {
    const result = document.createElementNS(NS, tag);
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        result.setAttribute(key, value);
      }
    });
    if (parent) parent.appendChild(result);
    return result;
  }

  function gradient(parent, id, colors, extra = {}) {
    const result = node('linearGradient', {
      id, x1: 0, y1: 0, x2: 0.15, y2: 1, ...extra
    }, parent);
    colors.forEach(([offset, color]) => {
      node('stop', {
        offset, 'stop-color': `var(--m-${color})`
      }, result);
    });
    return result;
  }

  const ornaments = {
    crown: 'M-14 6L-16-10L-7-2L0-14L7-2L16-10L14 6Z',
    heart: 'M0 9C-14 0-14-12-6-12C-2-12 0-9 0-7C0-9 2-12 6-12C14-12 14 0 0 9Z'
  };

  class Bracelet {
    constructor(svg, options = {}) {
      this.svg = svg;
      this.opts = { shine: false, shadow: true, ...options };
      this.uid = 'nbb' + (++serial);
      this.state = null;
      this.destroyed = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onMotion = () => {
        this._syncShine();
        if (this.motion.matches && window.gsap) {
          gsap.killTweensOf(this.root.querySelectorAll('.nb-name'));
          this.root.querySelectorAll('.nb-name').forEach(name => {
            name.style.opacity = '1';
          });
        }
      };
      this.motion.addEventListener?.('change', this.onMotion);
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg.setAttribute('role', 'img');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.classList.add('nb-bracelet');
      this._defs();
    }

    _defs() {
      const u = this.uid;
      this.svg.replaceChildren();
      const defs = node('defs', {}, this.svg);
      gradient(defs, u + '-link', [
        ['0', 'hi'], ['.25', 'light'], ['.5', 'mid'],
        ['.75', 'dark'], ['1', 'light']
      ]);
      gradient(defs, u + '-face', [
        ['0', 'hi'], ['.3', 'light'], ['.55', 'mid'],
        ['.75', 'light'], ['1', 'dark']
      ]);
      gradient(defs, u + '-side', [
        ['0', 'edge'], ['1', 'dark']
      ]);

      this.shineGradient = node('linearGradient', {
        id: u + '-shine',
        gradientUnits: 'userSpaceOnUse',
        x1: 0, y1: 0, x2: 200, y2: 80
      }, defs);
      [['0', '0'], ['.5', '.45'], ['1', '0']].forEach(
        ([offset, opacity]) => node('stop', {
          offset, 'stop-color': '#fff', 'stop-opacity': opacity
        }, this.shineGradient)
      );

      const shadow = node('filter', {
        id: u + '-shadow',
        x: '-5%', y: '-35%', width: '110%', height: '180%'
      }, defs);
      node('feDropShadow', {
        dx: 0, dy: 3, stdDeviation: 2.5,
        'flood-color': '#20160a', 'flood-opacity': '.2'
      }, shadow);

      this.clip = node('clipPath', { id: u + '-clip' }, defs);
      this.root = node('g', {
        class: 'nb-bracelet__root',
        filter: this.opts.shadow ? `url(#${u}-shadow)` : null
      }, this.svg);
      this.shine = node('rect', {
        x: 0, y: 0, width: W, height: H,
        fill: `url(#${u}-shine)`,
        'clip-path': `url(#${u}-clip)`,
        class: 'nb-bracelet__shine',
        'pointer-events': 'none'
      }, this.svg);
      this._syncShine();
    }

    _syncShine() {
      this.shineGradient.querySelector('animateTransform')?.remove();
      const enabled = this.opts.shine && !this.motion.matches;
      this.shine.setAttribute('display', enabled ? 'inline' : 'none');
      if (enabled) {
        node('animateTransform', {
          attributeName: 'gradientTransform',
          type: 'translate',
          values: '-400 0;1500 0;1500 0',
          keyTimes: '0;.45;1',
          dur: '6s',
          repeatCount: 'indefinite'
        }, this.shineGradient);
      }
    }

    setColor(key) {
      const color = NB.config.colors[key] || NB.config.colors.gold;
      Object.entries(color.m).forEach(([name, value]) => {
        this.svg.style.setProperty('--m-' + name, value);
      });
    }

    render(source) {
      if (this.destroyed) return;
      const state = {
        names: (source.names || ['']).map(name => String(name ?? '')),
        color: source.color || 'gold',
        font: source.font || '03'
      };
      this.state = state;
      const font = NB.util.fontById(state.font);
      this.setColor(state.color);
      this._draw(state, font);
      if (!NB.fonts.isLoaded(font)) {
        NB.fonts.ensure(font).then(ok => {
          if (!this.destroyed && this.state === state) {
            this.svg.dataset.fontLoaded = String(ok);
            this._draw(state, font);
          }
        });
      }
      this.svg.setAttribute('aria-label',
        window.CariamText("copy.text_230", "[v0]のブレスレット参考プレビュー。名前の順番：[v1]。書体 #[v2]（参考表示）。", {v0: NB.util.colorLabel(state.color), v1: state.names.join('・') || window.CariamText("copy.text_229", '未入力'), v2: font.id})
      );
    }

    _link(parent, x, index) {
      const group = node('g', {
        transform: `translate(${x.toFixed(2)} ${curveY(x).toFixed(2)}) `
          + `rotate(${(curveA(x) + (index % 2 ? 28 : -28)).toFixed(2)})`
      }, parent);
      node('ellipse', {
        rx: 10.5, ry: 6, fill: 'none',
        stroke: 'var(--m-dark)', 'stroke-width': 5.4
      }, group);
      node('ellipse', {
        rx: 10.5, ry: 6, fill: 'none',
        stroke: `url(#${this.uid}-link)`, 'stroke-width': 3.8
      }, group);
      node('path', {
        d: 'M-7-4.6Q0-7.5 7-4.6', fill: 'none',
        stroke: 'var(--m-hi)', 'stroke-width': .8,
        'stroke-linecap': 'round', opacity: .7
      }, group);
    }

    _draw(state, font) {
      const u = this.uid;
      this.root.replaceChildren();
      this.clip.replaceChildren();

      const names = state.names.length ? state.names : [''];
      const items = names.map(text => {
        // Reserve vertical room for ornaments within the same height budget.
        const targetHeight = font.orn ? 42 : 52;
        const probe = NB.fonts.metrics(text || 'Name', font, 64);
        let size = Math.min(
          76, 64 * targetHeight / probe.height
        ) * Math.min(1, font.scale || 1);
        let metrics = NB.fonts.metrics(text || 'Name', font, size);
        if (metrics.width > 280) {
          size *= 280 / metrics.width;
          metrics = NB.fonts.metrics(text || 'Name', font, size);
        }
        return {
          text, size, metrics,
          width: Math.max(26, metrics.width + 5)
        };
      });

      const minimumGap = 58;
      const rawWidth = items.reduce((sum, item) => sum + item.width, 0)
        + minimumGap * (items.length - 1);
      // Never enlarge a short name just to fill the available space.
      const scale = Math.min(1, 850 / rawWidth);
      const gap = minimumGap * scale;
      const total = items.reduce(
        (sum, item) => sum + item.width * scale, 0
      ) + gap * (items.length - 1);
      let cursor = midpoint - total / 2;
      items.forEach(item => {
        item.start = cursor;
        item.end = cursor + item.width * scale;
        item.center = (item.start + item.end) / 2;
        cursor = item.end + gap;
      });

      const chain = node('g', { class: 'nb-chain' }, this.root);
      let index = 0;
      for (let x = X0 + 10; x <= X1 - 70; x += 12.5) {
        if (!items.some(item =>
          x > item.start - 3 && x < item.end + 3
        )) {
          this._link(chain, x, index);
        }
        index += 1;
      }

      const tag = node('g', {
        transform: `translate(${X0} ${curveY(X0)}) rotate(${curveA(X0)})`
      }, this.root);
      node('circle', {
        r: 9, fill: `url(#${u}-face)`,
        stroke: 'var(--m-dark)', 'stroke-width': 1.2
      }, tag);

      const claspX = X1 - 40;
      const clasp = node('g', {
        transform: `translate(${claspX} ${curveY(claspX)}) `
          + `rotate(${curveA(claspX)})`
      }, this.root);
      node('circle', {
        cx: -16, r: 7, fill: 'none',
        stroke: `url(#${u}-link)`, 'stroke-width': 3.5
      }, clasp);
      node('path', {
        d: 'M-8 0C-6-15 26-17 32-2C36 8 20 15 4 10C-2 8-8 6-8 0Z',
        fill: `url(#${u}-face)`,
        stroke: 'var(--m-dark)', 'stroke-width': 1.2
      }, clasp);
      node('path', {
        d: 'M2-6C10-10 22-10 26-3',
        fill: 'none', stroke: 'var(--m-hi)',
        'stroke-width': 1, opacity: .8
      }, clasp);
      node('rect', {
        x: 12, y: 6, width: 8, height: 5, rx: 2,
        fill: 'var(--m-dark)', opacity: .6
      }, clasp);

      const layer = node('g', { class: 'nb-names' }, this.root);
      const css = NB.fonts.css(font);

      items.forEach((item, nameIndex) => {
        const transform =
          `translate(${item.center.toFixed(2)} ${curveY(item.center).toFixed(2)}) `
          + `rotate(${curveA(item.center).toFixed(2)}) scale(${scale.toFixed(5)})`;
        const group = node('g', {
          transform, class: 'nb-name', 'data-name-index': nameIndex,
          'data-letter-height': (item.metrics.height * scale).toFixed(2)
        }, layer);

        // Center ink bounds vertically without changing the customer's text.
        const baseline = (item.metrics.ascent - item.metrics.descent) / 2;
        const attrs = {
          x: 0, y: baseline,
          'text-anchor': 'middle',
          'font-family': css.fontFamily,
          'font-weight': css.fontWeight,
          'font-style': css.fontStyle,
          'font-size': item.size.toFixed(3),
          'stroke-linejoin': 'round'
        };

        if (!item.text) {
          const placeholder = node('text', {
            ...attrs, fill: 'none', stroke: 'var(--m-mid)',
            'stroke-width': .8, 'stroke-dasharray': '3 3', opacity: .6
          }, group);
          placeholder.textContent = window.CariamText("copy.text_231", '名前');
          return;
        }

        [-1, 1].forEach(side => {
          node('circle', {
            cx: side * (item.width / 2 + 1), cy: 0, r: 5,
            fill: 'none', stroke: `url(#${u}-link)`,
            'stroke-width': 2.7
          }, group);
        });

        const side = node('text', {
          ...attrs, y: baseline + .9,
          fill: `url(#${u}-side)`,
          stroke: 'var(--m-dark)', 'stroke-width': .9
        }, group);
        side.textContent = item.text;

        const face = node('text', {
          ...attrs,
          class: 'nb-name__face',
          fill: `url(#${u}-face)`,
          stroke: 'var(--m-edge)', 'stroke-width': .55,
          'paint-order': 'stroke'
        }, group);
        face.textContent = item.text;

        const highlight = node('text', {
          ...attrs, y: baseline - .25,
          fill: 'none', stroke: 'var(--m-hi)',
          'stroke-width': .25, opacity: .45
        }, group);
        highlight.textContent = item.text;

        const inkTop = -item.metrics.height / 2;
        if (ornaments[font.orn]) {
          const ornamentX = font.orn === 'crown'
            ? -item.width / 2 + 10
            : item.width / 2 - 5;
          const ornamentY = inkTop - 2;
          node('path', {
            d: ornaments[font.orn],
            transform: `translate(${ornamentX} ${ornamentY}) scale(.48)`,
            fill: `url(#${u}-face)`,
            stroke: 'var(--m-edge)', 'stroke-width': .8
          }, group);
        } else if (font.orn === 'rings') {
          [-1, 1].forEach(direction => node('circle', {
            cx: direction * (item.width / 2 - 3),
            cy: inkTop + 2, r: 4,
            fill: 'none', stroke: `url(#${u}-face)`,
            'stroke-width': 1.8
          }, group));
        }

        const clipText = node('text', {
          ...attrs, transform
        }, this.clip);
        clipText.textContent = item.text;
      });
    }

    pulse() {
      if (this.motion.matches || !window.gsap || this.destroyed) return;
      const names = this.root.querySelectorAll('.nb-name');
      if (names.length) {
        gsap.fromTo(names, { opacity: .65 }, {
          opacity: 1, duration: .25, ease: 'power2.out',
          stagger: .025, overwrite: true
        });
      }
    }

    destroy() {
      this.destroyed = true;
      this.motion.removeEventListener?.('change', this.onMotion);
      if (window.gsap) {
        gsap.killTweensOf(this.root.querySelectorAll('.nb-name'));
      }
      this.shineGradient.querySelector('animateTransform')?.remove();
    }
  }

  NB.Bracelet = Bracelet;

  NB.initStaticBracelets = function (scope = document) {
    scope.querySelectorAll('svg[data-bracelet-static]').forEach(svg => {
      if (svg.__nb) return;
      svg.__nb = new Bracelet(svg, {
        shine: false
      });
      svg.__nb.render({
        names: (svg.dataset.names || 'Emma').split(','),
        color: svg.dataset.color || 'gold',
        font: svg.dataset.font || '03'
      });
    });
  };
})();
